import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  getV1MeRetrieveQueryOptions,
  getV1QuizAdminQuestionsListQueryKey,
  useV1QuizAdminCapabilities,
  useV1QuizAdminQuestionsCreate,
} from '@/api/client'
import { QuizQuestionTypeEnum } from '@/api/model'
import type { AdminQuestion, AdminQuestionCreateRequest } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { QuestionForm } from '@/components/quiz-bank/question-form'
import { QuizAuthoringGate } from '@/components/quiz/quiz-capability-notice'
import { quizCapabilitiesOrClosed } from '@/components/quiz/quiz-capabilities'
import { typeLabel } from '@/lib/quiz-labels'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_panel/pytania/nowe')({
  beforeLoad: async ({ context }) => {
    // Operator-only area (admin + superadmin, broader than konta-administracyjne):
    // everyone else is bounced out before any question data loads (FR-002).
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: NewQuestionPage,
})

const TYPE_DESCRIPTIONS: Record<QuizQuestionTypeEnum, string> = {
  [QuizQuestionTypeEnum.pairs]: 'Tytuł zestawu i od 3 do 6 par termin–definicja.',
  [QuizQuestionTypeEnum.trueFalse]: 'Stwierdzenie z wartością logiczną i opcjonalnym wyjaśnieniem.',
  [QuizQuestionTypeEnum.singleChoice]:
    'Pytanie z 4 odpowiedziami, z których dokładnie jedna jest poprawna.',
}

/**
 * US2 create editor (T017): a three-tile type chooser switching the per-type
 * form. Submit creates a DRAFT (revision 1) and navigates to the new
 * question's detail; a 400 lands on the matching fields and creates nothing
 * (FR-005–FR-009, FR-015).
 */
function NewQuestionPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const capabilitiesQuery = useV1QuizAdminCapabilities()
  const capabilities = quizCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const [type, setType] = useState<QuizQuestionTypeEnum>(QuizQuestionTypeEnum.pairs)
  const createMutation = useV1QuizAdminQuestionsCreate()

  const handleSubmit = async (payload: AdminQuestionCreateRequest) => {
    const response = await createMutation.mutateAsync({ data: payload })
    if (response.status === 201) {
      await queryClient.invalidateQueries({ queryKey: getV1QuizAdminQuestionsListQueryKey() })
      const created = response.data as AdminQuestion
      await navigate({ to: '/pytania/$uid', params: { uid: created.uid } })
    }
    return response
  }

  return (
    <>
      <PageHeader title="Nowe pytanie" />
      <div className="flex max-w-3xl flex-col gap-6 p-6">
        <QuizAuthoringGate capabilities={capabilities}>
          <fieldset className="grid gap-3 sm:grid-cols-3">
            <legend className="mb-2 text-sm font-medium">Typ pytania</legend>
            {Object.values(QuizQuestionTypeEnum).map((token) => (
              <button
                key={token}
                type="button"
                onClick={() => setType(token)}
                aria-pressed={type === token}
                className={cn(
                  'flex flex-col gap-1 rounded-xl border bg-background p-4 text-left transition-colors',
                  type === token
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'hover:border-muted-foreground/40',
                )}
              >
                <span className="text-sm font-semibold">{typeLabel(token)}</span>
                <span className="text-xs text-muted-foreground">{TYPE_DESCRIPTIONS[token]}</span>
              </button>
            ))}
          </fieldset>

          {/* Remount the editor on a type switch so stale values never leak
              between the per-type forms (each owns its own useForm). */}
          <QuestionForm
            key={type}
            type={type}
            mode="create"
            submitLabel="Utwórz szkic"
            onSubmit={handleSubmit}
            onCancel={() => void navigate({ to: '/pytania' })}
          />
        </QuizAuthoringGate>
      </div>
    </>
  )
}
